// Dependencies
import { IBoltTaskRule, useBoltApiClient } from "@bolt/core";
import {
  dialogStore,
  PrimaryButton,
  PrimaryGrid,
  PrimaryGridModule,
  utils,
} from "@ucl/library";
import { useRef } from "react";
import { TaskRuleGridConfig } from "./config/TaskRuleGridConfig";
import { useEffect } from "react";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { CreateBoltTaskRuleForm } from "../TaskRuleForm/CreateTaskRuleForm";
import { EditBoltTaskRuleForm } from "../TaskRuleForm/EditTaskRuleForm";

interface BoltTaskRuleGridProps {
  apiBaseUrl: string;
  gridKey: string;
  getAuthRequestHeader: () => Promise<Record<string, string>>;
  taskViewsSettings?: Partial<{
    odataUrl: string;
    gridTitle: string;
    gridAutoName: string;
    shouldCreateAutoView: boolean;
    defaultGridViewTitle: string;
    showGridViewPicker: boolean;
    showUserDefaultView: boolean;
  }>;
  gridLastRefreshed?: number;
}

export const BoltTaskRuleGrid: React.FC<BoltTaskRuleGridProps> = ({
  apiBaseUrl,
  gridKey,
  getAuthRequestHeader,
  taskViewsSettings,
  gridLastRefreshed,
}) => {
  const primaryGridModule = useRef<PrimaryGridModule>(null);
  const boltApiClient = useBoltApiClient();

  useEffect(() => {
    if (gridLastRefreshed) {
      primaryGridModule.current?.getGridModule()?.getGridRef()?.refresh();
    }
  }, [gridLastRefreshed]);

  const dialogId = "add-bolt-task-rule-dialog";
  const editDialogId = "edit-bolt-task-rule-dialog";

  return (
    <section className="bolt-task-rule-grid">
      <div className="bolt-task-rule-grid-header">
        <PrimaryButton
          text="Add Task Rule"
          iconProps={{
            icon: faPlus,
          }}
          onClick={() => {
            dialogStore.openDialog({
              id: dialogId,
              header: "Add Task Rule",
              allowDragging: false,
              height: "600px",
              width: "1200px",
              content: () => (
                <CreateBoltTaskRuleForm
                  apiBaseUrl={apiBaseUrl}
                  getAuthRequestHeader={getAuthRequestHeader}
                  onSave={() => {
                    primaryGridModule.current
                      ?.getGridModule()
                      .getGridRef()
                      ?.refresh();
                    dialogStore.closeDialog(dialogId);
                  }}
                  onCancel={() => {
                    dialogStore.closeDialog(dialogId);
                  }}
                />
              ),
              close: () => {
                dialogStore.closeDialog(dialogId);
              },
            });
          }}
        />
      </div>
      <div className="bolt-task-rule-grid-content">
        <PrimaryGrid<IBoltTaskRule, {}>
          gridKey={gridKey || "bolt-task-rule-grid"}
          ref={primaryGridModule}
          apiClient={boltApiClient}
          viewsSettings={{
            gridTitle: "Task Rule",
            odataUrl: `${utils.strings.trimEndForwardSlashForURL(
              apiBaseUrl
            )}/bolt/userview/grid`,
            ...taskViewsSettings,
          }}
          odataUrl={`${utils.strings.trimEndForwardSlashForURL(
            apiBaseUrl
          )}/odata/bolttaskruleodata`}
          getAuthRequestHeader={getAuthRequestHeader}
          columnsConfiguration={TaskRuleGridConfig}
          gridConfiguration={{
            allowExcelExport: true,
            onRecordClick: (ev) => {
              // ON CLICK WE NEED TO OPEN THE TASKS DIALOG IN EDIT MODE?
              if (ev && ev.rowData.id) {
                // actions.onRecordClick(ev.rowData.id);
                dialogStore.openDialog({
                  id: editDialogId,
                  header: "Edit Task Rule",
                  allowDragging: false,
                  height: "600px",
                  width: "1200px",
                  content: () => (
                    <EditBoltTaskRuleForm
                      apiBaseUrl={apiBaseUrl}
                      getAuthRequestHeader={getAuthRequestHeader}
                      taskRuleId={ev.rowData.id || ""}
                      onSave={() => {
                        primaryGridModule.current
                          ?.getGridModule()
                          .getGridRef()
                          ?.refresh();
                        dialogStore.closeDialog(editDialogId);
                      }}
                      onCancel={() => {
                        dialogStore.closeDialog(editDialogId);
                      }}
                    />
                  ),
                });
              }
            },
          }}
          showGridFilters
        />
      </div>
    </section>
  );
};
